import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, ExternalLink, Maximize2, Minimize2 } from 'lucide-react';
import { liveSyncApi } from '../services/liveSyncApi';
import { toast } from 'sonner';
import type { LiveSyncState, LiveSyncSyncMessage } from '../types';

interface LiveSyncViewerProps {
    tableId: string;
    isMaster?: boolean;
    userId?: string;
    websocket?: WebSocket | null;
    lastMessage?: any;
}

export function LiveSyncViewer({ tableId, isMaster = false, userId, websocket, lastMessage }: LiveSyncViewerProps) {
    const [state, setState] = useState<LiveSyncState>({ isActive: false, broadcastType: 'custom' });
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Carregar estado inicial
    useEffect(() => {
        const loadState = async () => {
            try {
                const initialState = await liveSyncApi.getStatus(tableId);
                console.log('[LIVESYNC] Estado inicial:', initialState);
                if (initialState.isActive) {
                    setState(initialState);
                }
            } catch (error) {
                console.error('[LIVESYNC] Erro ao carregar estado:', error);
            }
        };
        if (tableId) loadState();
    }, [tableId]);

    // Ouvir atualizações via prop lastMessage (centralizado no GameSession)
    useEffect(() => {
        if (!lastMessage) return;

        console.log('[LIVESYNC] Processando mensagem da prop:', lastMessage);
        const syncData = lastMessage as LiveSyncSyncMessage;

        // Verificar se a mensagem é para esta mesa
        if (syncData.data?.table_id && syncData.data.table_id !== tableId) {
            console.log('[LIVESYNC] Ignorando mensagem de outra mesa:', syncData.data.table_id);
            return;
        }

        if (syncData.action === 'start') {
            setState({
                isActive: true,
                contentUrl: syncData.data.url,
                embedCode: syncData.data.embed_code,
                broadcastType: syncData.data.type
            });
            toast.info('Sincronização iniciada pelo mestre');
        } else if (syncData.action === 'stop') {
            setState({ isActive: false, broadcastType: 'custom' });
            toast.info('Sincronização encerrada');
            setIsFullscreen(false);
        }
    }, [lastMessage, tableId]);

    const handleStop = async () => {
        try {
            console.log('[LIVESYNC] Solicitando encerramento...');
            await liveSyncApi.stopLiveSync(tableId);
            // O estado será atualizado pelo WebSocket para todos, incluindo o mestre
        } catch (error) {
            console.error('[LIVESYNC] Erro ao parar sincronização:', error);
            toast.error('Erro ao encerrar a sincronização');
        }
    };

    if (!state.isActive) return null;


    return (
        <Dialog open={state.isActive} onOpenChange={(open) => {
            // Somente o mestre pode fechar mudando o estado do componente (muda via stopsig)
            if (!open && isMaster) {
                handleStop();
            }
        }}>
            <DialogContent
                className={`bg-game-secondary border-game-primary p-0 overflow-hidden flex flex-col transition-all duration-300 [&>button:last-child]:hidden ${isFullscreen ? 'max-w-none w-screen h-screen rounded-none' : 'w-[95vw] max-w-[1400px] h-[90vh] sm:max-w-none'
                    }`}
                // Desabilitar o botão de fechar padrão do Dialog (X) para alunos
                onInteractOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
            >
                <DialogHeader className="p-4 border-b border-game-primary flex flex-row items-center justify-between space-y-0">
                    <div className="flex items-center gap-2">
                        <DialogTitle className="text-game-primary">Sincronização Ativa</DialogTitle>
                        {state.contentUrl && (
                            <a
                                href={state.contentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1"
                            >
                                <ExternalLink className="h-3 w-3" />
                                Link Original
                            </a>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setIsFullscreen(!isFullscreen)}
                            className="text-game-muted hover:text-game-primary"
                        >
                            {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                        </Button>

                        {isMaster && (
                            <Button
                                variant="destructive"
                                size="sm"
                                onClick={handleStop}
                                className="ml-2"
                            >
                                Encerrar para Todos
                            </Button>
                        )}
                    </div>
                </DialogHeader>

                <div className="flex-1 bg-black relative">
                    {state.embedCode ? (
                        <div
                            className="w-full h-full [&>iframe]:w-full [&>iframe]:h-full"
                            dangerouslySetInnerHTML={{ __html: state.embedCode }}
                        />
                    ) : (
                        <iframe
                            src={state.contentUrl}
                            className="w-full h-full border-0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                            referrerPolicy="strict-origin-when-cross-origin"
                            // Removemos o sandbox para vídeos (especialmente YouTube) pois causa Erro 153 em alguns players/redes
                            sandbox={state.broadcastType === 'video' ? undefined : "allow-scripts allow-same-origin allow-forms allow-popups allow-presentation allow-popups-to-escape-sandbox"}
                            title="Sincronização Ativa"
                        />
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
